import { configManager } from '@/Core/Managers/Config'
import { Table as AntTable, Button, Space, Tooltip } from 'antd'
import { observer } from 'mobx-react-lite'
import React, { useEffect } from 'react'
import { reaction } from 'mobx'
import { AiOutlineDelete } from 'react-icons/ai'
import { StringInput } from '../Inputs/StringInput'
import { useTableField } from '../Providers/FormHooks'
import { BaseFieldStore } from '../Stores/BaseFieldStore'
import { TableFieldStore } from '../Stores/TableFieldStore'

interface FormTableFieldProps {
    /** Назва поля таблиці (альтернатива field) */
    name?: string
    /** Поле таблиці (альтернатива name) */
    field?: TableFieldStore
    /** Показувати як таблицю (за замовчуванням) або як список */
    layout?: 'table' | 'list'
    /** Кастомний рендер рядка */
    renderRow?: (row: any, index: number, field: TableFieldStore) => React.ReactNode
    /** Додаткові пропси для Table компонента */
    tableProps?: any
}

/**
 * Компонент для відображення TABLE поля форми
 * 
 * TABLE поле - це масив об'єктів, де кожен об'єкт є рядком таблиці.
 * Використовується TableFieldStore для управління рядками.
 * 
 * @example
 * ```tsx
 * // Використання з name (рекомендовано)
 * <FormTableField name="products" layout="table" />
 * 
 * // Або з field
 * const field = useTableField('products')
 * <FormTableField field={field} layout="table" />
 * ```
 */
/**
 * Компонент-обгортка для вкладеного поля таблиці з підтримкою depFn
 */
const NestedFieldCell = observer(({ nestedField, formContext, error, showLabel }: {
    nestedField: BaseFieldStore
    formContext: any
    error?: string
    showLabel: boolean
}) => {
    const formConfig: any = configManager.get('FormConfig')
    const fieldType = nestedField.type.toLowerCase()
    const FieldInput = formConfig.fieldTypes[fieldType] || formConfig.fieldTypes['string'] || StringInput

    useEffect(() => {
        const depFn = (nestedField as any).depFn
        if (typeof depFn !== 'function' || formContext.isEditable === false) return

        const execute = () => {
            try { depFn({ formContext, field: nestedField }) } catch (e) { console.error('depFn error:', e) }
        }
        execute()
        const dispose = reaction(
            () => JSON.stringify(formContext.values),
            () => execute()
        )
        return () => dispose()
    }, [nestedField, formContext])

    return (
        <div className="form-field" style={{ marginBottom: 0 }}>
            {showLabel && nestedField.label && (
                <label style={{ display: 'block', marginBottom: '4px', fontSize: '14px' }}>
                    {nestedField.label}
                    {nestedField.isRequired && <span style={{ color: 'red' }}> *</span>}
                </label>
            )}
            <Tooltip title={error} color="#ff4d4f">
                <div style={error ? { borderRadius: '6px', boxShadow: '0 0 0 2px rgba(255,77,79,0.3)', border: '1px solid #ff4d4f' } : undefined}>
                    <FieldInput field={nestedField} status={error ? 'error' : undefined} />
                </div>
            </Tooltip>
            {showLabel && error && (
                <div className="field-error" style={{ color: '#ff4d4f', fontSize: '12px', marginTop: '4px' }}>
                    {error}
                </div>
            )}
        </div>
    )
})

export const FormTableField: React.FC<FormTableFieldProps> = observer(
    ({ name, field: externalField, layout = 'table', renderRow, tableProps = {} }) => {
        // Отримуємо поле через хук якщо передано name
        const fieldFromHook = name ? useTableField(name) : undefined
        const field = externalField || fieldFromHook

        if (!field) {
            return (
                <div style={{ color: 'red', padding: '16px', border: '1px solid red', borderRadius: '4px' }}>
                    Поле {name || 'не вказано'} не знайдено або не є TABLE полем
                </div>
            )
        }

        // Читаємо errors тут, щоб MobX відстежував зміни і перерендерил таблицю
        const formErrors: Record<string, string> = field.formContext?.errors ?? {}

        const isEditable = field.formContext?.isEditable !== false

        /**
         * Рендер окремого поля без використання FormField
         * Це запобігає дублюванню шляху в імені поля
         * @param showLabel - показувати label (для списку true, для таблиці false)
         */
        const renderNestedField = (nestedField: BaseFieldStore, _parentField: TableFieldStore, showLabel = true) => {
            const fieldPath = nestedField.path.join('.')
            const error = formErrors[fieldPath]
            return (
                <NestedFieldCell
                    key={nestedField.name}
                    nestedField={nestedField}
                    formContext={field.formContext}
                    error={error}
                    showLabel={showLabel}
                />
            )
        }        /**
         * Рендер значення поля у вигляді тексту (для режиму перегляду)
         */
        const renderFieldValue = (nestedField: BaseFieldStore) => {
            let value = nestedField.value
            if (value === null || value === undefined || value === '') return '—'
            if (typeof value === 'object' && !Array.isArray(value)) {
                return value.title || value['@id'] || '—'
            }
            if (typeof value === 'boolean') return value ? 'Так' : 'Ні'
            return String(value)
        }

        // Відображення у вигляді списку (картки)
        if (layout === 'list') {
            return (
                <div style={{ display: 'flex', flexDirection: 'column', gap: '16px' }}>
                    {field.rows.map((row, index) => (
                        <div
                            key={index}
                            style={{
                                border: '1px solid #d9d9d9',
                                borderRadius: '8px',
                                padding: '16px',
                                position: 'relative',
                            }}
                        >
                            <div
                                style={{
                                    position: 'absolute',
                                    top: '8px',
                                    right: '8px',
                                    display: 'flex',
                                    gap: '8px',
                                }}
                            >
                                <Button
                                    size="small"
                                    danger
                                    type="text"
                                    icon={<AiOutlineDelete style={{ fontSize: 18 }} />}
                                    onClick={() => field.removeRow(index)}
                                />
                            </div>

                            {renderRow ? (
                                renderRow(row, index, field)
                            ) : (
                                <div style={{ display: 'flex', flexDirection: 'column', gap: '12px' }}>
                                    {Object.keys(field.definition.fields || {}).map((fieldName) => {
                                        const nestedField = row.getNestedField(fieldName)
                                        if (!nestedField) return null
                                        
                                        return renderNestedField(nestedField, field)
                                    })}
                                </div>
                            )}
                        </div>
                    ))}

                    <Button
                        type="dashed"
                        onClick={() => field.addRow({})}
                        style={{ width: '100%' }}
                    >
                        + Додати рядок
                    </Button>
                </div>
            )
        }

        // Відображення у вигляді таблиці (за замовчуванням)
        const fieldNames = Object.keys(field.definition.fields || {})

        // Витягуємо extraColumns з tableProps, щоб не передавати їх до AntTable
        const { extraColumns: extraCols = [], columnWidths, ...restTableProps } = tableProps

        // Ширина колонок за типом поля
        const columnWidthByType: Record<string, number> = {
            switch: 120,
            select: 200,
            number: 140,
            string: 160,
            status: 180,
            textarea: 200,
            date: 150,
            datetime: 170,
        }

        // Автоматична генерація колонок з field definitions
        const columns = [
            {
                title: '№',
                key: 'index',
                width: 50,
                fixed: 'left' as const,
                render: (_: any, __: any, index: number) => index + 1,
            },
            ...fieldNames.map((fieldName) => {
                const fieldDef = field.definition.fields?.[fieldName]
                const colWidth = (columnWidths?.[fieldName]) ?? columnWidthByType[fieldDef?.type?.toLowerCase() || 'string'] ?? 160
                return {
                    title: (
                        <span>
                            {fieldDef?.label || fieldName}
                            {isEditable && fieldDef?.isRequired && <span style={{ color: 'red' }}> *</span>}
                        </span>
                    ),
                    key: fieldName,
                    dataIndex: fieldName,
                    width: colWidth,
                    render: (_: any, record: any, rowIndex: number) => {
                        // Підтримка кастомного рендера клітинки через fieldDef.renderCell
                        if (!isEditable && typeof (fieldDef as any)?.renderCell === 'function') {
                            return (fieldDef as any).renderCell(record[fieldName], record, rowIndex)
                        }

                        const row = field.getRow(rowIndex)
                        const nestedField = row?.getNestedField(fieldName)
                        
                        if (!nestedField) {
                            return <div style={{ color: 'red' }}>Поле не знайдено</div>
                        }

                        if (!isEditable) {
                            return renderFieldValue(nestedField)
                        }
                        
                        return renderNestedField(nestedField, field, false)
                    },
                }
            }),
            // Додаткові колонки (для відображення даних вкладених колекцій)
            ...extraCols,
            ...(isEditable ? [{
                title: 'Дії',
                key: 'actions',
                width: 40,
                render: (_: any, __: any, index: number) => (
                    <Space>
                        <Button
                            size="small"
                            danger
                            type="text"
                            icon={<AiOutlineDelete style={{ fontSize: 18 }} />}
                            onClick={() => field.removeRow(index)}
                        />
                    </Space>
                ),
            }] : []),
        ]

        return (
            <div>
                {isEditable && (
                <div style={{ marginBottom: '16px', display: 'flex', justifyContent: 'space-between', alignItems: 'center' }}>
                    <Button
                        type="dashed"
                        onClick={() => field.addRow({})}
                    >
                        + Додати рядок
                    </Button>
                    <span style={{ color: '#666' }}>
                        Всього рядків: <strong>{field.rowCount}</strong>
                    </span>
                </div>
                )}

                <AntTable
                    columns={columns}
                    dataSource={field.rows.map((_, index) => ({
                        key: index,
                        ...field.getRowData(index),
                    }))}
                    pagination={false}
                    bordered
                    size="small"
                    scroll={{ x: 'max-content' }}
                    {...restTableProps}
                />
            </div>
        )
    }
)
